// pages/Contacts.jsx
import React, { useState, useEffect } from "react";
import {
  Search,
  RefreshCw,
  User,
  Phone,
  Mail,
  Building2,
  TrendingUp,
  Users,
  Briefcase,
} from "lucide-react";
import { Add } from "iconsax-reactjs";
import apiService from "../../services/api";
import { useNavigate } from "react-router-dom";
import CreateContactModal from "../../components/CreateContactModal";
import { useTheme } from "../../contexts/ThemeContext";
import PhoneLink from "../../components/PhoneLink";

const Contacts = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({
    totalContacts: 0,
    recentContacts: 0,
    contactsByAccount: 0,
    contactsBySource: {},
  });
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    leadSource: "",
    search: "",
  });
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchContacts();
    fetchStats();
  }, [pagination.page, filters]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await apiService.getContacts({
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      });
      setContacts(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiService.getContactStats();
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleCreateSuccess = () => {
    fetchContacts();
    fetchStats();
    setShowCreateModal(false);
  };

  const StatCard = ({ title, value, change, icon: Icon }) => (
    <div
      className={`rounded-xl p-6 border transition-colors ${
        isDarkMode
          ? "bg-[#0C0E12] border-gray-700/50 hover:border-gray-600/50"
          : "bg-white border-gray-200 hover:border-gray-300"
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <div
          className={`text-[14px] font-medium ${
            isDarkMode ? "text-[#94979C]" : "text-gray-500"
          }`}
        >
          {title}
        </div>
        {Icon && (
          <Icon
            size={20}
            className={isDarkMode ? "text-gray-400" : "text-gray-400"}
          />
        )}
      </div>
      <div className="space-y-2">
        <div className="flex items-end justify-between">
          <div
            className={`text-[28px] font-bold ${
              isDarkMode ? "text-[#F7F7F7]" : "text-gray-900"
            }`}
          >
            {value}
          </div>
          {change !== undefined && (
            <div
              className={`flex items-center text-sm font-medium ${
                isDarkMode ? "text-white" : "text-gray-700"
              }`}
            >
              <TrendingUp className="mr-1 text-green-400" size={20} />
              {change}%
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const ContactRow = ({ contact }) => {
    const fullName =
      `${contact.firstName || ""} ${contact.lastName || ""}`.trim() ||
      "Unknown";
    const initials = fullName
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);

    return (
      <tr
        className={`border-b transition-colors cursor-pointer ${
          isDarkMode
            ? "border-gray-700 hover:bg-gray-800/50"
            : "border-gray-200 hover:bg-gray-50"
        }`}
        onClick={() => navigate(`/contacts/${contact.id}`)}
      >
        <td className="py-4 px-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-white font-semibold">
              {initials}
            </div>
            <div>
              <div
                className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {contact.salutation && `${contact.salutation} `}
                {fullName}
              </div>
              {contact.title && (
                <div
                  className={`text-xs flex items-center gap-1 ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  <Briefcase size={12} />
                  {contact.title}
                </div>
              )}
            </div>
          </div>
        </td>
        <td className="py-4 px-4">
          {contact.account ? (
            <div
              className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/accounts/${contact.account.id}`);
              }}
            >
              <Building2 size={14} />
              <span className="hover:underline">{contact.account.name}</span>
            </div>
          ) : (
            <span className={isDarkMode ? "text-gray-500" : "text-gray-400"}>
              —
            </span>
          )}
        </td>
        <td className="py-4 px-4">
          {contact.email ? (
            <a
              href={`mailto:${contact.email}`}
              className="text-blue-400 hover:text-blue-300 transition-colors hover:underline flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <Mail size={14} />
              {contact.email}
            </a>
          ) : (
            <span className={isDarkMode ? "text-gray-500" : "text-gray-400"}>
              —
            </span>
          )}
        </td>
        <td className="py-4 px-4">
          {contact.phone || contact.mobilePhone ? (
            <PhoneLink
              phoneNumber={contact.phone || contact.mobilePhone}
              entityType="contact"
              entityId={contact.id}
              entityName={`${contact.firstName} ${contact.lastName}`}
              className="hover:underline"
            />
          ) : (
            <span className={isDarkMode ? "text-gray-500" : "text-gray-400"}>
              —
            </span>
          )}
        </td>
        <td className="py-4 px-4">
          {contact.leadSource && (
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full border text-xs font-medium ${
                isDarkMode
                  ? "bg-gray-500/10 text-gray-400 border-gray-500/20"
                  : "bg-gray-100 text-gray-600 border-gray-200"
              }`}
            >
              {contact.leadSource}
            </span>
          )}
        </td>
        <td className="py-4 px-4">
          {contact.owner ? (
            <div className="flex items-center gap-2">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                  isDarkMode
                    ? "bg-gray-700 text-gray-300"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                {contact.owner.name.charAt(0)}
              </div>
              <span
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {contact.owner.name}
              </span>
            </div>
          ) : (
            <span className={isDarkMode ? "text-gray-500" : "text-gray-400"}>
              Unassigned
            </span>
          )}
        </td>
        <td
          className={`py-4 px-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
        >
          {new Date(contact.createdAt).toLocaleDateString()}
        </td>
      </tr>
    );
  };

  return (
    <div
      className={`min-h-screen font-inter ${
        isDarkMode ? "bg-[#0A0B0F] text-white" : "bg-gray-50 text-gray-900"
      }`}
    >
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="flex-1 min-w-0">
            <h1
              className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Contacts
            </h1>
            <p
              className={`text-sm sm:text-base leading-relaxed ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Manage your business contacts and relationships
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-2 sm:gap-3 shrink-0">
            <button
              onClick={fetchContacts}
              className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg font-medium transition-colors text-sm sm:text-base min-w-0 ${
                isDarkMode
                  ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:bg-gray-700"
                  : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
              }`}
            >
              <RefreshCw size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Refresh</span>
            </button>

            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base min-w-0"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                border: "1px solid",
                borderImageSource:
                  "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                boxShadow: `
                  0px 36px 32px -16px #427BCA36,
                  0px 8px 16px 0px #2F578F1F,
                  0px 2px 4px -8px #427BCA14,
                  0px 8px 24px -16px #FFFFFF3D inset,
                  0px -24px 32px 0px #FFFFFF38 inset,
                  0px 0px 12px 0px #FFFFFF inset
                `,
              }}
            >
              <Add size={16} className="shrink-0" />
              <span className="whitespace-nowrap">New Contact</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative">
            <input
              type="text"
              placeholder="Search contacts..."
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
              className={`pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:border-gray-400 ${
                isDarkMode
                  ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-400"
                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
              }`}
            />
            <Search
              size={16}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
          </div>

          <select
            value={filters.leadSource}
            onChange={(e) =>
              setFilters({ ...filters, leadSource: e.target.value })
            }
            className={`px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-gray-400 ${
              isDarkMode
                ? "bg-[#0C0E12] border-gray-700 text-white"
                : "bg-white border-gray-300 text-gray-900"
            }`}
          >
            <option value="">All Sources</option>
            <option value="Web">Web</option>
            <option value="Phone Inquiry">Phone Inquiry</option>
            <option value="Partner Referral">Partner Referral</option>
            <option value="Purchased List">Purchased List</option>
            <option value="Other">Other</option>
          </select>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Contacts"
            value={stats.totalContacts}
            change={12}
            icon={Users}
          />
          <StatCard
            title="New This Month"
            value={stats.recentContacts}
            change={8}
            icon={TrendingUp}
          />
          <StatCard
            title="With Accounts"
            value={stats.contactsByAccount}
            icon={Building2}
          />
          <StatCard
            title="Active Contacts"
            value={stats.totalContacts}
            icon={User}
          />
        </div>

        {/* Table */}
        <div
          className={`rounded-lg border mb-8 ${
            isDarkMode
              ? "bg-[#0C0E12] border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          <div
            className={`flex items-center justify-between p-6 border-b ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div>
              <h2
                className={`text-xl font-semibold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                All Contacts{" "}
                <span
                  className={`text-sm ml-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {pagination.total} contacts
                </span>
              </h2>
              <p
                className={`text-sm mt-1 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                View and manage all your business contacts
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px]">
              <thead className={isDarkMode ? "bg-[#13161B]" : "bg-gray-50"}>
                <tr
                  className={`border-b ${
                    isDarkMode ? "border-[#22262F]" : "border-gray-200"
                  }`}
                >
                  {[
                    "Name",
                    "Account",
                    "Email",
                    "Phone",
                    "Lead Source",
                    "Owner",
                    "Created",
                  ].map((h) => (
                    <th
                      key={h}
                      className={`text-left py-4 px-4 font-medium text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="7"
                      className={`py-8 text-center ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Loading contacts...
                    </td>
                  </tr>
                ) : contacts.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className={`py-8 text-center ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      No contacts found. Create your first contact to get
                      started.
                    </td>
                  </tr>
                ) : (
                  contacts.map((contact) => (
                    <ContactRow key={contact.id} contact={contact} />
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div
            className={`flex flex-col sm:flex-row sm:items-center justify-between p-6 border-t gap-4 ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="flex gap-2">
              <button
                onClick={() =>
                  setPagination({ ...pagination, page: pagination.page - 1 })
                }
                disabled={pagination.page === 1}
                className={`px-3 py-1 border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDarkMode
                    ? "bg-[#0C0E12] border-[#373A41] text-gray-300 hover:bg-gray-600"
                    : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
                }`}
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setPagination({ ...pagination, page: pagination.page + 1 })
                }
                disabled={pagination.page >= pagination.totalPages}
                className={`px-3 py-1 border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDarkMode
                    ? "bg-[#0C0E12] border-[#373A41] text-gray-300 hover:bg-gray-600"
                    : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
                }`}
              >
                Next
              </button>
            </div>
            <span
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Page {pagination.page} of {pagination.totalPages}
            </span>
          </div>
        </div>
      </div>

      {showCreateModal && (
        <CreateContactModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}
    </div>
  );
};

export default Contacts;
